import type { EventDetailsSummaryData } from '../event-details-summary/EventDetailsSummary';
import type { SpotAvailabilityLineData } from '../event-details-summary/SpotAvailabilityLine';
import type {
  BookEventBranch,
  BookEventHostData,
  BookEventPayload,
  BookEventPortalConfig,
} from './bookEvent.types';
import {
  EVENT_TYPES,
  getAttendancePolicyMessage,
  hasSpotAvailabilityPrerequisites,
  isGoingToWaitingList,
  translateOrFallback,
} from './bookEventRules';
import { isBookAppointment, type BookingOrchestrationVariant } from './bookingDialogVariant';
import { t } from '../../services/locale';

const DATE_FORMAT = 'ddd, MMM Do';

function formatDate(
  timestamp: number | undefined,
  formatUnix: BookEventHostData['formatUnix']
): string {
  return timestamp ? formatUnix(timestamp, DATE_FORMAT) : '';
}

function formatTime(
  timestamp: number | undefined,
  branch: BookEventBranch | null,
  formatUnix: BookEventHostData['formatUnix']
): string {
  const hourFormat = branch?.configuration?.formats?.time || 'HH:mm';
  return timestamp ? formatUnix(timestamp, hourFormat) : '';
}

function formatWaitingListLine(
  event: BookEventPayload,
  branch: BookEventBranch | null
): string {
  const waitingSize = branch?.features?.booking?.waiting_list || 0;
  return `${event.waiting}/${waitingSize} ${t('WEB_ON_WAITING_LIST')}`;
}

function joinTrainerNames(
  event: BookEventPayload,
  shorten: boolean,
  shortenTrainerName: BookEventHostData['shortenTrainerName']
): string {
  const trainers = (event.trainers_obj || []).map((trainer) => {
    const named = shorten ? shortenTrainerName(trainer) : trainer;
    return `${named.first_name || ''} ${named.last_name || ''}`.trim();
  });
  return trainers.join(', ');
}

export function getEventDate(
  event: BookEventPayload,
  formatUnix: BookEventHostData['formatUnix']
): string {
  if (event.type === EVENT_TYPES.CLASS) {
    return formatDate(event.time_start, formatUnix);
  }
  if (event.type === EVENT_TYPES.COURSE && event.scheduleToBook) {
    const start = formatDate(event.scheduleToBook.start_date, formatUnix);
    const end = formatDate(event.scheduleToBook.end_date, formatUnix);
    return start && end ? `${start} - ${end}` : start + end;
  }
  if (event.type === EVENT_TYPES.TIMESLOT) {
    return formatDate(event.timeslotToBook?.time_start, formatUnix);
  }
  return '';
}

export function getEventStartTime(
  event: BookEventPayload,
  branch: BookEventBranch | null,
  formatUnix: BookEventHostData['formatUnix']
): string {
  if (event.type === EVENT_TYPES.CLASS) {
    return formatTime(event.time_start, branch, formatUnix);
  }
  if (event.type === EVENT_TYPES.TIMESLOT) {
    return formatTime(event.timeslotToBook?.time_start, branch, formatUnix);
  }
  return '';
}

export function getEventDuration(event: BookEventPayload): number | string {
  if (event.type === EVENT_TYPES.CLASS) {
    return event.duration || '';
  }
  if (event.type === EVENT_TYPES.TIMESLOT) {
    return event.timeslotToBook?.duration || '';
  }
  return '';
}

export function getTotalBookingsAndSize(
  event: BookEventPayload,
  portalConfig: BookEventPortalConfig
): string {
  const counts = bookingCounts(event);
  if (!counts) {
    return '';
  }
  return formatBookingCounts(counts, portalConfig);
}

function bookingCounts(event: BookEventPayload): { booked?: number; size?: number } | null {
  if (event.type === EVENT_TYPES.CLASS) {
    return event;
  }
  if (event.type === EVENT_TYPES.COURSE && event.scheduleToBook) {
    return event.scheduleToBook;
  }
  return null;
}

function formatBookingCounts(
  counts: { booked?: number; size?: number },
  portalConfig: BookEventPortalConfig
): string {
  const showTotal = portalConfig.isTotalBookingsEnabled;
  const showSize = portalConfig.isSizeEnabled;
  if (showTotal && showSize) {
    return `${counts.booked}/${counts.size} ${t('WEB_JOINED')}`;
  }
  if (showSize) {
    return `${counts.size} ${t('WEB_SLOTS')}`;
  }
  return showTotal ? `${counts.booked} ${t('WEB_JOINED')}` : '';
}

export function getTotalBookingsOnWaitingList(
  event: BookEventPayload,
  branch: BookEventBranch | null
): string {
  if (event.type === EVENT_TYPES.CLASS) {
    return formatWaitingListLine(event, branch);
  }
  return '';
}

export function getTrainersNames(
  event: BookEventPayload,
  shorten: boolean,
  shortenTrainerName: BookEventHostData['shortenTrainerName']
): string {
  if (event.type !== EVENT_TYPES.CLASS && event.type !== EVENT_TYPES.COURSE) {
    return '';
  }
  return joinTrainerNames(event, shorten, shortenTrainerName);
}

export function getCourseTime(
  day: { start_time?: number; end_time?: number },
  branch: BookEventBranch | null,
  formatUnix: BookEventHostData['formatUnix']
): string {
  const format = branch?.configuration?.formats?.time || 'HH:mm';
  if (day.start_time == null || day.end_time == null) {
    return '';
  }
  return `${formatUnix(day.start_time, format)} to ${formatUnix(day.end_time, format)}`;
}

export function buildSpotAvailabilityProps(args: {
  event: BookEventPayload;
  flags: BookEventHostData['flags'];
  userToken: string | null;
  envUrl: string;
  branch: BookEventBranch | null;
  isSpotBookingEnabledForEvent: boolean;
}): SpotAvailabilityLineData | null {
  if (!canBuildSpotAvailability(args)) {
    return null;
  }

  const availableLabel = translateOrFallback('WEB_AVAILABLE', 'Available');
  return {
    layoutId: String(args.event.layout_id),
    timeSlotId: String(args.event._id),
    envUrl: args.envUrl,
    userToken: args.userToken,
    availableSuffix: availableLabel.toLowerCase(),
    oneSpotAvailableLabel: translateOrFallback(
      'WEB_ONE_SPOT_AVAILABLE',
      '1 spot available'
    ),
    spotsAvailableCountLabel: translateOrFallback(
      'WEB_SPOTS_AVAILABLE_COUNT',
      '{count} spots available'
    ),
    availableFallbackLabel: availableLabel,
  };
}

function canBuildSpotAvailability(args: {
  event: BookEventPayload;
  flags: BookEventHostData['flags'];
  userToken: string | null;
  branch: BookEventBranch | null;
  isSpotBookingEnabledForEvent: boolean;
}): args is typeof args & { userToken: string } {
  if (!args.isSpotBookingEnabledForEvent || !args.userToken) {
    return false;
  }
  if (isGoingToWaitingList(args.event, args.branch)) {
    return false;
  }
  return hasSpotAvailabilityPrerequisites({
    event: args.event,
    flags: args.flags,
    userToken: args.userToken,
  });
}

export function buildEventDetailsSummary(args: {
  event: BookEventPayload;
  autoBookingIsEnabled: boolean;
  isGoingToWaitingList: boolean;
  portalConfig: BookEventPortalConfig;
  eventDate: string;
  startTime: string;
  duration: number | string;
  bookingsLine: string;
  waitingListLine: string;
  facilityName: string;
  trainersLine: string | null;
  scheduleDays: EventDetailsSummaryData['scheduleDays'];
  showAttendancePolicy: boolean;
  attendancePolicyMessage: string;
}): EventDetailsSummaryData {
  const eventType = args.event.type;
  const showTimeRow = eventType !== 'course';

  return {
    eventDate: args.eventDate,
    showTimeRow,
    timeRow: buildTimeRow(args, showTimeRow),
    showTimeslotMeta: eventType !== 'timeslot',
    bookingsLine: args.bookingsLine,
    showWaitingListBlock: shouldShowWaitingListBlock(args),
    waitingListLine: args.waitingListLine,
    waitingPositionLine: buildWaitingPositionLine(args),
    levelLine: buildLevelLine(args),
    trainersLine: args.trainersLine,
    facilityLine: buildFacilityLine(args),
    showCourseSchedule: eventType === 'course',
    viewScheduleLabel: t('WEB_VIEW_SCHEDULE'),
    scheduleDays: args.scheduleDays || [],
    showAttendancePolicy: args.showAttendancePolicy,
    attendancePolicyTitle: t('WEB_ATTENDANCE_POLICY'),
    attendancePolicyMessage: args.attendancePolicyMessage,
  };
}

type EventDetailsArgs = Parameters<typeof buildEventDetailsSummary>[0];

function buildTimeRow(args: EventDetailsArgs, showTimeRow: boolean): string {
  return showTimeRow
    ? `${args.startTime} - ${args.duration} ${t('WEB_MINUTES')}`
    : '';
}

function buildWaitingPositionLine(args: EventDetailsArgs): string | null {
  if (!args.autoBookingIsEnabled || !args.event.position) {
    return null;
  }
  return `${args.event.position} ${t('WEB_ON_WAITING_LIST')}`;
}

function shouldShowWaitingListBlock(args: EventDetailsArgs): boolean {
  if (!args.portalConfig.isTotalWaitingListEnabled) {
    return false;
  }
  if (args.event.type === EVENT_TYPES.COURSE) {
    return false;
  }
  return args.isGoingToWaitingList;
}

function buildLevelLine(args: EventDetailsArgs): string | null {
  return args.portalConfig.isLevelEnabled ? args.event.level || null : null;
}

function buildFacilityLine(args: EventDetailsArgs): string | null {
  return args.portalConfig.isFacilityNameEnabled ? args.facilityName || '' : null;
}

type SummaryLines = Pick<
  EventDetailsArgs,
  'eventDate' | 'startTime' | 'duration' | 'bookingsLine' | 'waitingListLine' | 'trainersLine'
>;

function showsTrainerLine(data: BookEventHostData): boolean {
  return data.portalConfig.isTrainerNameEnabled && data.event.type !== EVENT_TYPES.COURSE;
}

function eventSummaryLines(data: BookEventHostData): SummaryLines {
  const event = data.event;
  return {
    eventDate: getEventDate(event, data.formatUnix),
    startTime: getEventStartTime(event, data.branch, data.formatUnix),
    duration: getEventDuration(event),
    bookingsLine: getTotalBookingsAndSize(event, data.portalConfig),
    waitingListLine: getTotalBookingsOnWaitingList(event, data.branch),
    trainersLine: showsTrainerLine(data)
      ? getTrainersNames(
          event,
          data.portalConfig.isShortTrainerNameEnabled,
          data.shortenTrainerName
        )
      : null,
  };
}

// Appointments read the timeslot fields straight off the event and fold the
// trainer names into the date line instead of rendering their own row.
function appointmentSummaryLines(data: BookEventHostData): SummaryLines {
  const event = data.event;
  const trainers = showsTrainerLine(data)
    ? joinTrainerNames(
        event,
        data.portalConfig.isShortTrainerNameEnabled,
        data.shortenTrainerName
      )
    : '';

  return {
    eventDate: [formatDate(event.time_start, data.formatUnix), trainers]
      .filter(Boolean)
      .join('\n'),
    startTime: formatTime(event.time_start, data.branch, data.formatUnix),
    duration: event.duration || '',
    bookingsLine: formatBookingCounts(event, data.portalConfig),
    waitingListLine: formatWaitingListLine(event, data.branch),
    trainersLine: null,
  };
}

function summaryLinesFor(
  variant: BookingOrchestrationVariant,
  data: BookEventHostData
): SummaryLines {
  if (isBookAppointment(variant)) {
    return appointmentSummaryLines(data);
  }
  return eventSummaryLines(data);
}

function isAutoBookingEnabled(data: BookEventHostData): boolean {
  return data.branch?.features?.booking?.auto_booking_enabled === true;
}

function facilityNameFrom(data: BookEventHostData): string {
  return data.event.facility_obj?.name ?? '';
}

function attendancePolicyFrom(data: BookEventHostData): string {
  return getAttendancePolicyMessage({
    penalties: data.penalties,
    cancelWindow: data.branch?.features?.booking?.booking_cancel_window,
    currencySymbol: data.currencySymbol,
  });
}

export function buildDialogEventSummary(args: {
  data: BookEventHostData;
  variant: BookingOrchestrationVariant;
  scheduleDays: EventDetailsSummaryData['scheduleDays'];
  goingToWaitingList: boolean;
  showAttendancePolicy: boolean;
}): EventDetailsSummaryData {
  const { data } = args;

  return buildEventDetailsSummary({
    ...summaryLinesFor(args.variant, data),
    event: data.event,
    autoBookingIsEnabled: isAutoBookingEnabled(data),
    isGoingToWaitingList: args.goingToWaitingList,
    portalConfig: data.portalConfig,
    facilityName: facilityNameFrom(data),
    scheduleDays: args.scheduleDays,
    showAttendancePolicy: args.showAttendancePolicy,
    attendancePolicyMessage: attendancePolicyFrom(data),
  });
}
